import pulse from '@finn-no/pulse-sdk';
import { useSidebarContext } from '../state/SidebarContext';

/**
 * Tracking definitions can be found here
 * https://docs.google.com/spreadsheets/d/1RKvYMohVjuZVq1uUFYLwI2rw2873RhGo-DtOzvZ0Kk0/edit#gid=1552677307&range=B17
 */

/**
 * TJT_ev05
 * The user is viewing either the "Give bid"-button or "Give bid" + "Buy now"
 */
const trackViewButton = (finnkode: string, isPreviewMode: boolean, isBuyNow: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'View',
        intent: 'Contact',
        name: 'Purchase with delivery',
        object: {
            id: `tjt-create-offer-button-view-${isBuyNow ? 'buy-now' : 'bid'}`,
            name: isBuyNow ? 'buy now' : 'bid',
            type: 'UIElement',
            elementType: 'Button',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'n/a',
                    category: 'n/a',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
            subVertical: 'transaction_journey_recommerce',
        },
    });
};
/**
 * TJT_ev01
 * The user clicked either of the buttons: "Give bid" or "Buy now"
 */
const trackCreateOfferClick = (
    finnkode: string,
    isPreviewMode: boolean,
    clickedBuyNow: boolean,
) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'Click',
        intent: 'Contact',
        name: 'Purchase with delivery',
        object: {
            id: `tjt-create-offer-button-${clickedBuyNow ? 'buy-now' : 'bid'}`,
            name: clickedBuyNow ? 'buy now' : 'bid',
            type: 'UIElement',
            elementType: 'Button',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'n/a',
                    category: 'n/a',
                },
            ],
        },
        target: {
            id: finnkode,
            type: 'ProcessFlow',
            name: 'Transactionjourney recommerce',
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * TJT_ev17
 * "Inscreen event when "fiks ferdig podlet" is viewed on an ad with waiting message"
 *
 * Tracking that the user sees the message that confirms that they have suggested
 * for the seller to opt in for fiks ferdig.
 */
const trackViewSuggestionMade = (finnkode: string, isPreviewMode: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'Fiks ferdig',
        object: {
            id: `tjt-create-offer-button-view-already-suggested`,
            type: 'UIElement',
            elementType: 'Podlet text',
            position: 'Classified ad',
            name: 'Waiting on fiks ferdig',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'N/A',
                    category: 'N/A',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * TJT_ev18
 * "Inscreen event when "fiks ferdig podlet" is viewed on an ad with error message"
 *
 * When the user tries to suggest Fiks ferdig, but there is an api failure.
 */
const trackViewSuggestShippingFailed = (finnkode: string, isPreviewMode: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'Fiks ferdig',
        object: {
            id: `tjt-create-offer-button-view-suggested-failed`,
            type: 'UIElement',
            elementType: 'Podlet text',
            position: 'Classified ad',
            name: 'Unable to request fiks ferdig',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'N/A',
                    category: 'N/A',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * TJT_ev19
 * "When buyer clicks on "suggest shipping" ("Be om Fiks ferdig") on an ad to ask for purchase with delivery
 * when it is not enabled. This event is triggered when user confirms in modal."
 */

const trackConfirmSuggestShipping = (finnkode: string, isPreviewMode: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'Click',
        intent: 'Confirm',
        name: 'Suggest shipping',
        object: {
            id: `tjt-create-offer-button-click-suggest-shipping`,
            type: 'UIElement',
            elementType: 'Button',
            position: 'Classified ad',
            name: 'Request fiks ferdig',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'N/A',
                    category: 'N/A',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * TJT_ev25
 * When user clicks to read more about fiks ferdig
 */
const trackShowFiksFerdigAllInfo = (finnkode: string, isPreviewMode: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Fiks ferdig all info',
        object: {
            id: `tjt-create-offer-button-show-fiks-ferdig-all-info`,
            type: 'UIElement',
            elementType: 'Sidebar',
            position: 'Classified ad',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'N/A',
                    category: 'N/A',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * TJT_ev47
 * "When buyer sees "suggest shipping" ("Be om Fiks ferdig") button on an ad."
 */

const trackViewSuggestShipping = (finnkode: string, isPreviewMode: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'View',
        intent: 'Request',
        name: 'Suggest shipping',
        object: {
            id: `tjt-create-offer-button-click-suggest-shipping`,
            type: 'UIElement',
            elementType: 'Button',
            position: 'Classified ad',
            name: 'Request fiks ferdig',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'N/A',
                    category: 'N/A',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * TJT_ev48
 * "When buyer clicks on "suggest shipping" (be om fiks ferdig) on an ad to ask for purchase with delivery when
 * it is not enabled. This event is triggered when user opens the modal for the first time."
 */

const trackExpandSuggestShipping = (finnkode: string, isPreviewMode: boolean) => {
    if (isPreviewMode) return;

    pulse.trackEvent({
        type: 'Click',
        intent: 'Request',
        name: 'Suggest shipping',
        object: {
            id: `tjt-create-offer-button-click-suggest-shipping`,
            type: 'UIElement',
            elementType: 'Button',
            position: 'Classified ad',
            name: 'Request fiks ferdig',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: finnkode,
                    contentId: finnkode,
                    name: 'N/A',
                    category: 'N/A',
                },
            ],
        },
        vertical: {
            name: 'recommerce',
        },
    });
};

/**
 * Can only be used inside of the SideBarProvider
 */
const usePulse = () => {
    const { finnkode, isPreviewMode, uiText } = useSidebarContext();
    const isBuyNow = !!uiText.sections?.sidebar?.optedIn?.actions?.initTransactionBuyNow;

    return {
        trackViewButton: () => trackViewButton(finnkode, isPreviewMode, isBuyNow),
        trackCreateOfferClick: (clickedBuyNow: boolean) =>
            trackCreateOfferClick(finnkode, isPreviewMode, clickedBuyNow),
        trackViewSuggestionMade: () => trackViewSuggestionMade(finnkode, isPreviewMode),
        trackViewSuggestShippingFailed: () =>
            trackViewSuggestShippingFailed(finnkode, isPreviewMode),
        trackConfirmSuggestShipping: () => trackConfirmSuggestShipping(finnkode, isPreviewMode),
        trackShowFiksFerdigAllInfo: () => trackShowFiksFerdigAllInfo(finnkode, isPreviewMode),
        trackExpandSuggestShipping: () => trackExpandSuggestShipping(finnkode, isPreviewMode),
        trackViewSuggestShipping: () => trackViewSuggestShipping(finnkode, isPreviewMode),
    };
};

export default usePulse;
